import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import GdprConsentBlock from "../components/GdprConsentBlock";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MailCheck, CheckCircle2, Loader2 } from "lucide-react";

export default function ClientRegister() {
  const { registerClient, resendVerification, apiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name_salone: "",
    referente_nome: "",
    referente_cognome: "",
    city: "",
    province: "",
    positioning: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  // v1.36 double opt-in: swap the form for a success panel once submitted.
  const [pendingEmail, setPendingEmail] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Devi accettare Privacy Policy e Note Legali per registrarti");
      return;
    }
    setLoading(true);
    try {
      const res = await registerClient({
        email: form.email.trim(),
        password: form.password,
        name_salone: form.name_salone.trim(),
        referente_nome: form.referente_nome.trim(),
        referente_cognome: form.referente_cognome.trim(),
        city: form.city.trim(),
        province: form.province.trim().toUpperCase(),
        positioning: form.positioning.trim(),
        consents: {
          terms_accepted: true,
          marketing_accepted: !!acceptedMarketing,
          policy_version: "2026-05",
        },
      });
      setPendingEmail(res.email || form.email.trim());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      side="client"
      title="Crea account — Cliente"
      subtitle="Registra il tuo salone e il referente per iniziare."
      altLinkLabel="Hai già un account? Accedi →"
      altLinkTo="/cliente/login"
      altLinkDataTestId="client-register-to-login"
    >
      {pendingEmail ? (
        <ClientCheckEmailPanel
          email={pendingEmail}
          onResend={() => resendVerification(pendingEmail)}
          apiError={apiError}
        />
      ) : (
      <form onSubmit={onSubmit} data-testid="client-register-form" className="space-y-4">
        <div>
          <Label htmlFor="name_salone" className="text-xs font-bold uppercase tracking-[0.18em]">
            Nome salone
          </Label>
          <Input
            id="name_salone"
            data-testid="client-register-salon"
            required
            value={form.name_salone}
            onChange={update("name_salone")}
            className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
            placeholder="Es. Salone Luna"
          />
        </div>

        <div className="grid grid-cols-[1fr_120px] gap-3">
          <div>
            <Label htmlFor="city" className="text-xs font-bold uppercase tracking-[0.18em]">
              Città
            </Label>
            <Input
              id="city"
              data-testid="client-register-city"
              required
              value={form.city}
              onChange={update("city")}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
              placeholder="Es. Pavia"
            />
          </div>
          <div>
            <Label htmlFor="province" className="text-xs font-bold uppercase tracking-[0.18em]">
              Provincia
            </Label>
            <Input
              id="province"
              data-testid="client-register-province"
              required
              maxLength={4}
              value={form.province}
              onChange={update("province")}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)] uppercase"
              placeholder="PV"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="positioning" className="text-xs font-bold uppercase tracking-[0.18em]">
            Di cosa sei esperto / di cosa ti occupi
          </Label>
          <Textarea
            id="positioning"
            data-testid="client-register-positioning"
            value={form.positioning}
            onChange={update("positioning")}
            rows={4}
            className="mt-2 rounded-xl border-[var(--mp-ink)] text-sm"
            placeholder="Es. Salone specializzato in colorazione naturale e balayage caldo, taglio uomo donna, ricostruzione capelli con prodotti vegani."
          />
          <p className="mt-1.5 text-[11px] text-[var(--mp-muted)]">
            Scrivi a tuo modo, anche poche parole. L'AI userà queste righe + città/provincia
            per generare un posizionamento professionale che alimenterà caption e hashtag.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="referente_nome" className="text-xs font-bold uppercase tracking-[0.18em]">
              Nome referente
            </Label>
            <Input
              id="referente_nome"
              data-testid="client-register-ref-name"
              required
              value={form.referente_nome}
              onChange={update("referente_nome")}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
              placeholder="Mario"
            />
          </div>
          <div>
            <Label htmlFor="referente_cognome" className="text-xs font-bold uppercase tracking-[0.18em]">
              Cognome referente
            </Label>
            <Input
              id="referente_cognome"
              data-testid="client-register-ref-surname"
              required
              value={form.referente_cognome}
              onChange={update("referente_cognome")}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
              placeholder="Rossi"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em]">
            Email
          </Label>
          <Input
            id="email"
            data-testid="client-register-email"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
            placeholder="salone@email.it"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em]">
            Password
          </Label>
          <Input
            id="password"
            data-testid="client-register-password"
            type="password"
            required
            minLength={8}
            pattern="^(?=.*[A-Za-z])(?=.*\d).{8,}$"
            title="Almeno 8 caratteri, con lettere e numeri"
            value={form.password}
            onChange={update("password")}
            className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
            placeholder="min. 8 caratteri, lettere e numeri"
          />
        </div>
        <GdprConsentBlock
          acceptedTerms={acceptedTerms}
          setAcceptedTerms={setAcceptedTerms}
          acceptedMarketing={acceptedMarketing}
          setAcceptedMarketing={setAcceptedMarketing}
          testidPrefix="client-register"
        />
        <Button
          type="submit"
          disabled={loading || !acceptedTerms}
          data-testid="client-register-submit"
          className="w-full h-12 rounded-full bg-[var(--mp-primary)] hover:bg-[var(--mp-primary-hover)] text-white"
        >
          {loading ? "Creazione account…" : "Crea account"}
        </Button>
      </form>
      )}
    </AuthShell>
  );
}

function ClientCheckEmailPanel({ email, onResend, apiError }) {
  const [resending, setResending] = useState(false);
  const [sentAgain, setSentAgain] = useState(false);
  const resend = async () => {
    setResending(true);
    try {
      await onResend();
      setSentAgain(true);
      toast.success("Ti abbiamo rimandato il link");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResending(false);
    }
  };
  return (
    <div
      data-testid="client-register-check-email"
      className="rounded-2xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)]/40 p-5 sm:p-6 text-center"
    >
      <div className="h-12 w-12 mx-auto rounded-full bg-[var(--mp-ink)] grid place-items-center">
        <MailCheck className="h-6 w-6 text-white" />
      </div>
      <h3 className="font-display text-2xl mt-3 leading-tight">
        Controlla la tua email
      </h3>
      <p className="text-sm text-[var(--mp-ink)] mt-2 leading-relaxed">
        Ti abbiamo mandato un link a{" "}
        <strong data-testid="client-register-check-email-address">{email}</strong>.
        Clicca il link entro <strong>24 ore</strong> per attivare il tuo account.
      </p>
      <p className="text-xs text-[var(--mp-muted)] mt-3">
        Se non lo trovi, controlla <strong>spam</strong> o <strong>promozioni</strong>.
      </p>
      <Button
        type="button"
        onClick={resend}
        disabled={resending || sentAgain}
        data-testid="client-register-resend"
        className="mt-4 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white disabled:opacity-60"
      >
        {resending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : sentAgain ? (
          <span className="inline-flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Rimandato
          </span>
        ) : (
          "Non ho ricevuto niente, rimandalo"
        )}
      </Button>
    </div>
  );
}
